import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/RichTextEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, Plus, Pencil, Trash2, ArrowLeft, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: '', description: '', url: '', category: '', sort_order: 0, active: true };

export default function RegionalAdminResources() {
  const [user, setUser] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [u, r] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/regional-admin/resources`, { withCredentials: true }),
      ]);
      setUser(u.data); setResources(r.data || []);
    } catch (e) { toast.error('Failed to load resources'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, sort_order: resources.length + 1 }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ title: r.title, description: r.description || '', url: r.url, category: r.category || '', sort_order: r.sort_order || 0, active: r.active }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!/^https?:\/\//.test(form.url.trim())) { toast.error('A valid http(s) URL is required'); return; }
    setSaving(true);
    try {
      if (editing) await axios.put(`${BACKEND_URL}/api/regional-admin/resources/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/regional-admin/resources`, form, { withCredentials: true });
      toast.success('Resource saved'); setOpen(false); await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    try { await axios.put(`${BACKEND_URL}/api/regional-admin/resources/${r.id}`, { active: !r.active }, { withCredentials: true }); await load(); }
    catch (err) { toast.error('Update failed'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/regional-admin/resources/${r.id}`, { withCredentials: true }); toast.success('Deleted'); await load(); }
    catch (err) { toast.error('Delete failed'); }
  };

  return (
    <Layout user={user}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/regional-admin')} data-testid="ra-res-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Regional Admin
        </button>
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center"><BookOpen className="w-5 h-5 text-brand" /></span>
            <div><h1 className="text-2xl font-bold text-slate-900 leading-tight">Manage Regional Services</h1>
              <p className="text-sm text-slate-500">Curate local services and helpful links for employers in your region</p></div>
          </div>
          <Button onClick={openNew} data-testid="ra-res-new" className="bg-brand hover:bg-brand-hover text-white"><Plus className="w-4 h-4 mr-1.5" /> New Service</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : resources.length === 0 ? (
          <Card className="p-12 text-center border border-slate-200"><p className="text-slate-700 font-medium">No regional services yet</p>
            <p className="text-sm text-slate-400 mt-1">Add your first service link for your region.</p></Card>
        ) : (
          <div className="space-y-3" data-testid="ra-res-list">
            {resources.map((r) => (
              <div key={r.id} data-testid={`ra-res-row-${r.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                <span className="w-8 h-8 rounded-md bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center flex-shrink-0">{r.sort_order}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900 truncate">{r.title}</h3>
                    {r.category && <span className="text-[10px] font-semibold uppercase bg-brand-light text-brand px-2 py-0.5 rounded-full">{r.category}</span>}
                    {!r.active && <span className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">inactive</span>}
                  </div>
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand hover:underline inline-flex items-center gap-1 mt-0.5 truncate max-w-full">{r.url} <ExternalLink className="w-3 h-3 flex-shrink-0" /></a>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button onClick={() => toggleActive(r)} data-testid={`ra-res-toggle-${r.id}`} variant="outline" size="sm" className="border-slate-200 text-xs">{r.active ? 'Deactivate' : 'Activate'}</Button>
                  <Button onClick={() => openEdit(r)} data-testid={`ra-res-edit-${r.id}`} variant="outline" size="sm" className="border-slate-200"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button onClick={() => remove(r)} data-testid={`ra-res-delete-${r.id}`} variant="outline" size="sm" className="border-slate-200 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="ra-res-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Regional Service' : 'New Regional Service'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-sm">Title</Label>
              <Input data-testid="ra-res-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="Resource title" /></div>
            <div><Label className="text-sm">Description</Label>
              <div className="mt-1">
                <RichTextEditor full testId="ra-res-description" value={form.description}
                  onChange={(html) => setForm({ ...form, description: html })}
                  placeholder="Short description. Use the toolbar for basic formatting." />
              </div></div>
            <div><Label className="text-sm">URL</Label>
              <Input data-testid="ra-res-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1" placeholder="https://example.org" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Category <span className="text-slate-400">(optional)</span></Label>
                <Input data-testid="ra-res-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" placeholder="e.g. Programs" /></div>
              <div><Label className="text-sm">Sort order</Label>
                <Input data-testid="ra-res-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="ra-res-active" />
              <span className="text-sm text-slate-700">Active (visible to employers)</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="ra-res-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
